/**
 * Configuration for a gradient color stop
 */
export class GradientStop {
    constructor(color, position) {
      this.color = color; // {r, g, b, a}
      this.position = position; // 0.0 to 1.0
    }
  }
  
  /**
   * Configuration for gradient background
   */
  export class GradientConfig {
    constructor(options = {}) {
      // Direction: 'vertical', 'horizontal', or angle in degrees
      this.direction = options.direction || 'vertical';
      
      // Array of color stops
      this.stops = options.stops || [
        new GradientStop({r: 0.0, g: 0.0, b: 0.0, a: 1.0}, 0.0),
        new GradientStop({r: 0.0, g: 0.0, b: 0.06, a: 0.85}, 0.15),
        new GradientStop({r: 0.0, g: 0.0, b: 0.04, a: 0.7}, 0.7),
        new GradientStop({r: 0.0, g: 0.0, b: 0.0, a: 1.0}, 1.0)
      ];
    }
  }
  
  /**
   * Configuration for a single star layer
   */
  export class StarLayerConfig {
    constructor(options = {}) {
      // Color configuration with integrated alpha
      this.colorBase = options.colorBase || { r: 0.8, g: 0.8, b: 0.8, a: 1.0 };
      this.colorRandom = options.colorRandom || { r: 0.2, g: 0.2, b: 0.2, a: 0.0 };
      
      // Size configuration
      this.sizeBase = options.sizeBase || 1.0;
      this.sizeRandom = options.sizeRandom || 1.0;
      
      // Star count for this layer
      this.starCount = options.starCount || 1500;
      
      // Rotation configuration
      this.rotationPeriod = options.rotationPeriod || 10000; // seconds for full rotation
    }
  }
  
  /**
   * Main StarField renderer class
   */
  export default class StarFieldRenderer {
    constructor(canvas, starLayerConfigs, gradientConfig, settings = {}) {
      this.canvas = canvas;
      this.gl = this.canvas.getContext('webgl', { alpha: false }) || this.canvas.getContext('experimental-webgl', { alpha: false });
      
      if (!this.gl) {
        console.error('WebGL not supported');
        this.fallbackToCanvas();
        return;
      }
      
      // Use provided configurations or default
      this.layerConfigs = starLayerConfigs || [];
      
      // Use provided gradient config or default
      this.gradientConfig = gradientConfig || new GradientConfig();
      
      // Settings with defaults
      this.settings = {
        antiAliasing: settings.antiAliasing !== undefined ? settings.antiAliasing : false
      };

      // Initialize status
      this.isActive = true;
      
      // Set up the renderer
      this.resizeCanvas();
      window.addEventListener('resize', this.resizeCanvas);
      
      this.createShaders();
      this.createGradient();
      this.createStars();
      
      this.lastTime = 0;
      // Initialize rotation angles for each layer
      this.layerRotations = this.layerConfigs.map(() => 0);
      
      // Start the animation loop
    this.animationFrame = requestAnimationFrame(time => this.animate(time));
    }
    
    fallbackToCanvas() {
      const ctx = this.canvas.getContext('2d');
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight;
      
      ctx.fillStyle = 'black';
      ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      
      for (let i = 0; i < 500; i++) {
        const x = Math.random() * this.canvas.width;
        const y = Math.random() * this.canvas.height;
        const radius = 0.5 + Math.random() * 1.5;
        
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fillStyle = 'white';
        ctx.fill();
      }
    }
    
    resizeCanvas() {
      if (!this.canvas) return;
      
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight;
      
      if (this.gl) {
        this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
      }
    }
    
    generateLayerRotationCode() {
      let code = '';
      for (let i = 0; i < this.layerConfigs.length; i++) {
        if (i === 0) {
          code += `if (aLayerIndex < 0.5) {\n`;
        } else if (i === this.layerConfigs.length - 1) {
          code += `} else {\n`;
        } else {
          code += `} else if (aLayerIndex < ${i + 0.5}) {\n`;
        }
        code += `  angle = uRotations[${i}];\n`;
      }
      code += '}';
      return code;
    }
    
    createShaders() {
      // Vertex shader program - supporting multiple layers
      const vsSource = `
        attribute vec2 aPosition;
        attribute float aSize;
        attribute vec4 aColor;
        attribute float aLayerIndex;
        
        uniform float uRotations[${this.layerConfigs.length}];
        
        varying vec4 vColor;
        varying float vPointSize;
        
        void main(void) {
          // Get the rotation angle for this star's layer
          float angle = 0.0;
          
          ${this.generateLayerRotationCode()}
          
          // Offset rotation center to 25% below the bottom edge
          vec2 rotationCenter = vec2(0.0, -1.25);
          vec2 offsetPos = aPosition - rotationCenter;
          
          // Apply rotation
          float s = sin(angle);
          float c = cos(angle);
          mat2 rotation = mat2(c, -s, s, c);
          vec2 rotatedPosition = rotation * offsetPos + rotationCenter;
          
          gl_Position = vec4(rotatedPosition, 0.0, 1.0);
          gl_PointSize = aSize;
          vColor = aColor;
          vPointSize = aSize;
        }
      `;
  
      // Fragment shader program with anti-aliasing support
      const fsSource = `
        precision mediump float;
        varying vec4 vColor;
        varying float vPointSize;
        
        uniform bool uAntiAliasing;
        
        void main(void) {
          float distance = length(gl_PointCoord - vec2(0.5, 0.5));
          
          if (distance > 0.5) {
              discard;
          }
          
          // Base alpha calculation combining the star's alpha with edge fade
          float edgeAlpha = 1.0 - distance * 2.0;
          float finalAlpha = vColor.a * edgeAlpha;
          
          // Anti-aliasing for small stars
          if (uAntiAliasing) {
            if (vPointSize < 2.0) {
              float smoothFactor = max(0.4, vPointSize / 2.0);
              finalAlpha *= smoothFactor;
            }
          }
          
          // If alpha is too low, discard the fragment completely
          if (finalAlpha < 0.01) {
              discard;
          }
          
          gl_FragColor = vec4(vColor.rgb, finalAlpha);
        }
      `;
  
      // Create star shader program
      const vertexShader = this.compileShader(vsSource, this.gl.VERTEX_SHADER);
      const fragmentShader = this.compileShader(fsSource, this.gl.FRAGMENT_SHADER);
      
      if (!vertexShader || !fragmentShader) {
        console.error('Failed to compile star shaders');
        return;
      }
      
      this.program = this.gl.createProgram();
      this.gl.attachShader(this.program, vertexShader);
      this.gl.attachShader(this.program, fragmentShader);
      this.gl.linkProgram(this.program);
      
      if (!this.gl.getProgramParameter(this.program, this.gl.LINK_STATUS)) {
        console.error('Unable to initialize the shader program: ' + this.gl.getProgramInfoLog(this.program));
        return;
      }
      
      // Get attribute and uniform locations
      this.program.aPosition = this.gl.getAttribLocation(this.program, 'aPosition');
      this.program.aSize = this.gl.getAttribLocation(this.program, 'aSize');
      this.program.aColor = this.gl.getAttribLocation(this.program, 'aColor');
      this.program.aLayerIndex = this.gl.getAttribLocation(this.program, 'aLayerIndex');
      this.program.uAntiAliasing = this.gl.getUniformLocation(this.program, 'uAntiAliasing');
      
      // Get uniform locations for each layer's rotation
      this.program.uRotations = [];
      for (let i = 0; i < this.layerConfigs.length; i++) {
        this.program.uRotations[i] = this.gl.getUniformLocation(this.program, `uRotations[${i}]`);
      }
      
      // Create gradient shaders
      this.createGradientShaders();
    }
    
    generateGradientFragmentShader() {
      // Generate dynamic fragment shader based on gradient configuration
      const stops = this.gradientConfig.stops;
      
      let fragmentShader = `
        precision mediump float;
        varying vec2 vTexCoord;
        
        void main(void) {
      `;
      
      // Add coordinate calculation based on direction
      if (this.gradientConfig.direction === 'vertical') {
        fragmentShader += 'float coord = vTexCoord.y;\n';
      } else if (this.gradientConfig.direction === 'horizontal') {
        fragmentShader += 'float coord = vTexCoord.x;\n';
      } else {
        // Handle angle (degrees) - convert to radians and use as float
        const angleRad = typeof this.gradientConfig.direction === 'number' ? 
                      this.gradientConfig.direction * Math.PI / 180.0 : 0.0;
        fragmentShader += `
          float angle = ${angleRad.toFixed(7)};
          float coord = cos(angle) * vTexCoord.x + sin(angle) * vTexCoord.y;
        `;
      }
      
      // Generate the color mixing code based on stops
      fragmentShader += 'vec4 color;\n';
      
      for (let i = 0; i < stops.length - 1; i++) {
        const currentStop = stops[i];
        const nextStop = stops[i + 1];
        
        // Only write the mix logic if there's a gap between stops
        if (nextStop.position > currentStop.position) {
          const currentColor = `vec4(${currentStop.color.r.toFixed(2)}, ${currentStop.color.g.toFixed(2)}, ${currentStop.color.b.toFixed(2)}, ${currentStop.color.a.toFixed(2)})`;
          const nextColor = `vec4(${nextStop.color.r.toFixed(2)}, ${nextStop.color.g.toFixed(2)}, ${nextStop.color.b.toFixed(2)}, ${nextStop.color.a.toFixed(2)})`;
          
          if (i === 0) {
            fragmentShader += `if (coord < ${nextStop.position.toFixed(2)}) {\n`;
          } else {
            fragmentShader += `else if (coord < ${nextStop.position.toFixed(2)}) {\n`;
          }
          
          fragmentShader += `  color = mix(${currentColor}, ${nextColor}, (coord - ${currentStop.position.toFixed(2)}) / ${(nextStop.position - currentStop.position).toFixed(2)});\n`;
          fragmentShader += '}\n';
        }
      }
      
      // Add final stop as else case
      const lastStop = stops[stops.length - 1];
      fragmentShader += `else {\n`;
      fragmentShader += `  color = vec4(${lastStop.color.r.toFixed(2)}, ${lastStop.color.g.toFixed(2)}, ${lastStop.color.b.toFixed(2)}, ${lastStop.color.a.toFixed(2)});\n`;
      fragmentShader += '}\n';
      
      fragmentShader += 'gl_FragColor = color;\n';
      fragmentShader += '}\n';
      
      return fragmentShader;
    }
    
    createGradientShaders() {
      // Generate dynamic gradient shader based on configuration
      const gradientVsSource = `
        attribute vec2 aPosition;
        varying vec2 vTexCoord;
        
        void main(void) {
          gl_Position = vec4(aPosition, 0.0, 1.0);
          vTexCoord = aPosition * 0.5 + 0.5;  // Convert to 0-1 range
        }
      `;
      
      const gradientFsSource = this.generateGradientFragmentShader();
      
      // Create gradient shader program
      const gradientVertexShader = this.compileShader(gradientVsSource, this.gl.VERTEX_SHADER);
      const gradientFragmentShader = this.compileShader(gradientFsSource, this.gl.FRAGMENT_SHADER);
      
      if (!gradientVertexShader || !gradientFragmentShader) {
        console.error('Failed to compile gradient shaders');
        return;
      }
      
      this.gradientProgram = this.gl.createProgram();
      this.gl.attachShader(this.gradientProgram, gradientVertexShader);
      this.gl.attachShader(this.gradientProgram, gradientFragmentShader);
      this.gl.linkProgram(this.gradientProgram);
      
      if (!this.gl.getProgramParameter(this.gradientProgram, this.gl.LINK_STATUS)) {
        console.error('Unable to initialize the gradient program: ' + this.gl.getProgramInfoLog(this.gradientProgram));
        return;
      }
      
      this.gradientProgram.aPosition = this.gl.getAttribLocation(this.gradientProgram, 'aPosition');
    }
    
    createGradient() {
      // Create a full-screen quad for the gradient
      const positions = [
        -1.0, -1.0,
         1.0, -1.0,
        -1.0,  1.0,
         1.0,  1.0
      ];
      
      this.gradientBuffer = this.gl.createBuffer();
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.gradientBuffer);
      this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(positions), this.gl.STATIC_DRAW);
    }
    
    compileShader(source, type) {
      const shader = this.gl.createShader(type);
      this.gl.shaderSource(shader, source);
      this.gl.compileShader(shader);
      
      if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
        console.error('An error occurred compiling the shaders: ' + this.gl.getShaderInfoLog(shader));
        this.gl.deleteShader(shader);
        return null;
      }
      
      return shader;
    }
    
    createStars() {
      // Calculate the total number of stars based on each layer's starCount
      const totalStars = this.layerConfigs.reduce((total, config) => {
        return total + (config.starCount || 1500);
      }, 0);
      
      const positions = [];
      const sizes = [];
      const colors = [];
      const layerIndices = [];
      
      // Calculate the minimum distance needed to ensure stars always cover the screen
      const rotationCenterY = -1.25;
      const distanceToCorner = Math.sqrt(2*2 + (2 + Math.abs(rotationCenterY))**2);
      
      // Create stars for each layer
      for (let layerIdx = 0; layerIdx < this.layerConfigs.length; layerIdx++) {
        const config = this.layerConfigs[layerIdx];
        const starCount = config.starCount || 1500;
        
        // Create the specified number of stars for this layer
        for (let i = 0; i < starCount; i++) {
          // Uniform distribution in Cartesian space
          const coverageArea = distanceToCorner * 1.1; // Add 10% margin for safety
          
          // Random position in a square area
          const x = (Math.random() * 2 - 1) * coverageArea;
          const y = (Math.random() * 2 - 1) * coverageArea;
          
          positions.push(x);
          positions.push(y);
          
          // Size based on config
          const size = config.sizeBase + Math.random() * config.sizeRandom;
          sizes.push(size);
          
          // Color based on config with integrated alpha
          const r = config.colorBase.r + Math.random() * config.colorRandom.r;
          const g = config.colorBase.g + Math.random() * config.colorRandom.g;
          const b = config.colorBase.b + Math.random() * config.colorRandom.b;
          const a = config.colorBase.a + Math.random() * (config.colorRandom.a || 0);
          
          // Ensure alpha is within valid range
          const clampedAlpha = Math.min(1.0, Math.max(0.0, a));
          colors.push(r, g, b, clampedAlpha);
          
          // Store layer index
          layerIndices.push(layerIdx);
        }
      }
      
      // Create buffer for positions
      this.positionBuffer = this.gl.createBuffer();
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.positionBuffer);
      this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(positions), this.gl.STATIC_DRAW);
      
      // Create buffer for sizes
      this.sizeBuffer = this.gl.createBuffer();
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.sizeBuffer);
      this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(sizes), this.gl.STATIC_DRAW);
      
      // Create buffer for colors
      this.colorBuffer = this.gl.createBuffer();
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.colorBuffer);
      this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(colors), this.gl.STATIC_DRAW);
      
      // Create buffer for layer indices
      this.layerBuffer = this.gl.createBuffer();
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.layerBuffer);
      this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(layerIndices), this.gl.STATIC_DRAW);
      
      this.starCount = totalStars;
    }
    
    animate(currentTime) {
      if (!this.isActive) return;
      
      // Calculate delta time
      currentTime *= 0.001; // Convert to seconds
      const deltaTime = currentTime - this.lastTime;
      this.lastTime = currentTime;
      
      // Update rotation angles for each layer
      for (let i = 0; i < this.layerConfigs.length; i++) {
        const config = this.layerConfigs[i];
        // Calculate angle increment based on rotation period
        const rotationSpeed = (2 * Math.PI) / config.rotationPeriod;
        this.layerRotations[i] += rotationSpeed * deltaTime;
      }
      
      // Render
      this.render();
      
      // Continue animation loop if component is still mounted
    this.animationFrame = requestAnimationFrame(time => this.animate(time));
    }
    
    render() {
      // Clear the canvas
      this.gl.clearColor(0.0, 0.0, 0.0, 1.0);
      this.gl.clear(this.gl.COLOR_BUFFER_BIT);
      
      // 1. Render the gradient background
      try {
        this.gl.useProgram(this.gradientProgram);
        
        // Set up the gradient quad
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.gradientBuffer);
        const positionLocation = this.gradientProgram.aPosition;
        
        this.gl.enableVertexAttribArray(positionLocation);
        this.gl.vertexAttribPointer(positionLocation, 2, this.gl.FLOAT, false, 0, 0);
        
        // Disable any potential conflicts
        this.gl.disable(this.gl.DEPTH_TEST);
        this.gl.disable(this.gl.BLEND);
        
        // Draw the quad
        this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
        
        // Clean up
        this.gl.disableVertexAttribArray(positionLocation);
      } catch(e) {
        console.error('Error rendering gradient:', e);
      }
      
      // 2. Now render the stars on top of the gradient
      try {
        this.gl.useProgram(this.program);
        
        // Set anti-aliasing flag
        if (this.program.uAntiAliasing !== undefined) {
          this.gl.uniform1i(this.program.uAntiAliasing, this.settings.antiAliasing ? 1 : 0);
        }
        
        // Update uniforms for rotation of each layer
        for (let i = 0; i < this.layerConfigs.length; i++) {
          if (this.program.uRotations[i] !== undefined) {
            this.gl.uniform1f(this.program.uRotations[i], this.layerRotations[i]);
          }
        }
        
        // Bind and set up the position attribute
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.positionBuffer);
        this.gl.vertexAttribPointer(this.program.aPosition, 2, this.gl.FLOAT, false, 0, 0);
        this.gl.enableVertexAttribArray(this.program.aPosition);
        
        // Bind and set up the size attribute
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.sizeBuffer);
        this.gl.vertexAttribPointer(this.program.aSize, 1, this.gl.FLOAT, false, 0, 0);
        this.gl.enableVertexAttribArray(this.program.aSize);
        
        // Bind and set up the color attribute (now with 4 components)
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.colorBuffer);
        this.gl.vertexAttribPointer(this.program.aColor, 4, this.gl.FLOAT, false, 0, 0);
        this.gl.enableVertexAttribArray(this.program.aColor);
        
        // Bind and set up the layer index attribute
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.layerBuffer);
        this.gl.vertexAttribPointer(this.program.aLayerIndex, 1, this.gl.FLOAT, false, 0, 0);
        this.gl.enableVertexAttribArray(this.program.aLayerIndex);
        
        // Enable blending for proper transparency
        this.gl.enable(this.gl.BLEND);
        this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);
        
        // Draw all stars
        this.gl.drawArrays(this.gl.POINTS, 0, this.starCount);
        
        // Cleanup
        this.gl.disableVertexAttribArray(this.program.aPosition);
        this.gl.disableVertexAttribArray(this.program.aSize);
        this.gl.disableVertexAttribArray(this.program.aColor);
        this.gl.disableVertexAttribArray(this.program.aLayerIndex);
        this.gl.disable(this.gl.BLEND);
      } catch(e) {
        console.error('Error rendering stars:', e);
      }
    }
    
    destroy() {
      this.isActive = false;
    window.removeEventListener('resize', this.resizeCanvas);
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
      
      // Clean up WebGL resources
      if (this.gl) {
        // Delete buffers
        const buffers = [this.positionBuffer, this.sizeBuffer, this.colorBuffer, 
                        this.layerBuffer, this.gradientBuffer];
        buffers.forEach(buffer => {
          if (buffer) this.gl.deleteBuffer(buffer);
        });
        
        // Delete shader programs
        if (this.program) this.gl.deleteProgram(this.program);
        if (this.gradientProgram) this.gl.deleteProgram(this.gradientProgram);
      }
    }
  }
  